// Requests & payments: asking for a mileage advance, an expense or a relay (money to a staff member
// to pay a till); the cashier paying what's approved; accounting for relays; the float.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, Loading } from "@/components/kit";
import api from "@/lib/api";
import { act, Attach, DateIn, Files, input, k0, n, Num, opts, Select, State, useCan, useLoad, useRegisters } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const KINDS = [
  { value: "advance", label: "Mileage / trip advance", perm: "float.request" },
  { value: "expense", label: "Expense (paid to who did the work / sold it)", perm: "expense.request" },
  { value: "relay", label: "Relay – send to staff to pay a till / paybill", perm: "expense.request" },
];

export default function Spend() {
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("new") === "1" ? "new" : "list");
  useEffect(() => { if (params.get("new") === "1") setTab("new"); }, [params]);
  const tabs = [
    can("float.request", "expense.request") && { value: "new", label: "New request" },
    { value: "list", label: "Requests & payments" },
    can("float.pay", "float.approve", "dash.owner") && { value: "relays", label: "Money with staff" },
    can("float.pay", "float.topup", "float.approve", "books.view") && { value: "float", label: "M-Pesa float" },
  ].filter(Boolean);
  return (
    <Page title="Requests & payments">
      <Tabs value={tab} onChange={(v) => { setTab(v); setParams({}); }} tabs={tabs} />
      {tab === "new" && <NewRequest params={params} done={() => { setParams({}); setTab("list"); }} />}
      {tab === "list" && <List />}
      {tab === "relays" && <Relays />}
      {tab === "float" && <Float />}
    </Page>
  );
}

function NewRequest({ params, done }) {
  const can = useCan();
  const regs = useRegisters(["trucks", "payees"]);
  const { data: cats } = useLoad("/costs/categories");
  const { data: trips } = useLoad("/trips", { status: undefined });
  const { data: quotes } = useLoad("/quotes", { status: "approved" });
  const startKind = params.get("kind") || (can("float.request") ? "advance" : "expense");
  const [r, setR] = useState({ kind: startKind, date: today(), category: startKind === "advance" ? "mileage" : "",
    trip_id: params.get("trip") || null, attachments: [], paid_from: "float" });
  const set = (k) => (v) => setR({ ...r, [k]: v });
  const quote = (quotes || []).find((q) => q.id === r.quote_id);
  const openTrips = (trips || []).filter((t) => ["dispatched", "loaded", "delivered"].includes(t.status));
  const approvedPayees = (regs.payees || []).filter((p) => p.status === "approved");
  return (
    <Card className="mt-4 max-w-3xl bg-white">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="What is it"><Select value={r.kind} empty={null} onChange={(v) => setR({ ...r, kind: v, category: v === "advance" ? "mileage" : r.category })}
          options={KINDS.filter((k) => can(k.perm))} /></Field>
        <Field label="Date"><DateIn value={r.date} onChange={set("date")} /></Field>
        <Field label="Trip"><Select value={r.trip_id} onChange={set("trip_id")} empty={r.kind === "advance" ? "— choose the trip —" : "Not for a trip"}
          options={openTrips.map((t) => ({ value: t.id, label: `${t.no} · ${t.truck} · ${t.route || "on its way"}` }))} /></Field>
        {r.kind !== "advance" && !r.trip_id && <Field label="Truck (or leave empty for yard / office)"><Select value={r.truck_id} onChange={set("truck_id")} options={opts(regs.trucks, "plate")} /></Field>}
        <Field label="Category"><Select value={r.category} onChange={(v) => setR({ ...r, category: v, quote_id: null })} disabled={r.kind === "advance"}
          options={(cats || []).map((c) => ({ value: c.key, label: c.label }))} /></Field>
        {["repairs", "outside_labour", "painting"].includes(r.category) ? (
          <Field label="Approved quote it's paid against"><Select value={r.quote_id} onChange={set("quote_id")}
            options={(quotes || []).filter((q) => q.category === r.category).map((q) => ({ value: q.id, label: `${q.payee} · ${q.truck || ""} · ${q.description} · left ${k0(q.left)}` }))} /></Field>
        ) : (
          <Field label={r.kind === "relay" ? "Sent to (staff member)" : "Paid to"}><Select value={r.payee_id} onChange={set("payee_id")}
            options={approvedPayees.map((p) => ({ value: p.id, label: `${p.name} · ${p.phone || p.till_no || p.paybill_no}` }))} /></Field>
        )}
        <Field label="Amount (KES)"><Num value={r.amount} onChange={set("amount")} /></Field>
        <Field label={r.kind === "relay" ? "Till / shop it's for" : "Details"}><input className={input} value={r.description || ""} onChange={(e) => set("description")(e.target.value)} /></Field>
      </div>
      {quote && <p className="mt-3 text-sm text-slate-600">Quote: {k0(quote.amount)}, already paid {k0(quote.paid)} — up to <b>{k0(quote.left)}</b> needs no more approval.</p>}
      <div className="mt-3"><Attach value={r.attachments} onChange={set("attachments")} label={quote ? "Photo of the finished work / receipt" : "Receipt or photo"} /></div>
      <div className="mt-4 flex gap-2">
        <Btn kind="primary" disabled={!n(r.amount) || !r.category} onClick={async () => {
          const d = await act(() => api.post("/costs/spend", { ...r, amount: n(r.amount) }),
            (x) => (x.status === "approved" ? "Within the allowance - ready for the cashier to pay" : "Sent for approval"));
          if (d) done();
        }}>Submit</Btn>
        <Btn onClick={done}>Cancel</Btn>
      </div>
    </Card>
  );
}

function List() {
  const can = useCan();
  const [f, setF] = useState({ status: "" });
  const { data, reload } = useLoad("/costs/spend", { status: f.status || undefined });
  const [accounting, setAccounting] = useState(null);
  return (
    <>
      <div className="mt-4 max-w-xs"><Field label="Status"><Select value={f.status} onChange={(v) => setF({ status: v || "" })} empty="All"
        options={[["pending", "Waiting approval"], ["approved", "Approved – to pay"], ["paid", "Paid"], ["rejected", "Rejected"], ["cancelled", "Cancelled"]].map(([value, label]) => ({ value, label }))} /></Field></div>
      {!data ? <Loading /> : (
        <Table minWidth={980} cols={[{ label: "Date" }, { label: "What" }, { label: "Truck / trip" }, { label: "Requested by" }, { label: "Amount", align: "right" }, { label: "Status" }, { label: "" }]}>
          {data.map((e) => (
            <Tr key={e.id}>
              <Td>{fmtDate(e.date)}</Td>
              <Td>{e.kind === "relay" ? "Relay: " : e.kind === "advance" ? "Advance: " : ""}{e.category_label}
                <div className="text-xs text-slate-500">{e.description} <Files ids={e.attachments} /></div>
                {e.flags?.length > 0 && <div className="text-xs text-rose-700">{e.flags.join("; ")}</div>}</Td>
              <Td>{e.truck}{e.trip ? ` · ${e.trip}` : ""}</Td>
              <Td>{e.requested_name}</Td>
              <Td right>{k0(e.amount)}{e.allowance != null && <div className="text-xs text-slate-500">allowed {k0(e.allowance)}</div>}</Td>
              <Td><State s={e.status} />{e.kind === "relay" && e.status === "paid" && <div className="text-xs">{e.relay_status === "open" ? <span className="text-rose-700">not accounted for</span> : `spent ${k0(e.spent)}`}</div>}</Td>
              <Td><div className="flex flex-wrap justify-end gap-2">
                {can("float.pay") && e.status === "approved" && (e.paid_from === "float" || e.paid_from === "mpesa") && !e.batch_id &&
                  <Link to="/payouts" className="inline-flex min-h-[40px] items-center rounded-md bg-emerald-800 px-3 text-sm font-semibold text-white">Pay from M-Pesa payouts</Link>}
                {e.status === "approved" && e.batch_id && <span className="text-xs text-slate-500">in an M-Pesa batch</span>}
                {can("float.pay") && e.status === "approved" && !e.batch_id && e.paid_from !== "float" && e.paid_from !== "mpesa" && <Btn kind="primary" onClick={async () => {
                  const ref = window.prompt("M-Pesa confirmation code (optional)") ?? null;
                  if (ref !== null && await act(() => api.post(`/costs/spend/${e.id}/pay`, { reference: ref }), "Paid")) reload();
                }}>Pay</Btn>}
                {e.kind === "relay" && e.status === "paid" && e.relay_status === "open" && <Btn onClick={() => setAccounting(e)}>Account for it</Btn>}
                {["pending", "approved"].includes(e.status) && <Btn onClick={async () => { if (window.confirm("Cancel this request?") && await act(() => api.post(`/costs/spend/${e.id}/cancel`), "Cancelled")) reload(); }}>Cancel</Btn>}
              </div></Td>
            </Tr>
          ))}
        </Table>
      )}
      {accounting && <AccountRelay e={accounting} done={() => { setAccounting(null); reload(); }} />}
    </>
  );
}

function AccountRelay({ e, done }) {
  const [f, setF] = useState({ spent: e.amount, returned: 0, attachments: [] });
  return (
    <Card title={`Account for ${k0(e.amount)} sent for: ${e.description}`} className="mt-4 max-w-2xl bg-white">
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Paid to the till"><Num value={f.spent} onChange={(v) => setF({ ...f, spent: v })} /></Field>
        <Field label="Change sent back to the float"><Num value={f.returned} onChange={(v) => setF({ ...f, returned: v })} /></Field>
        <Field label="Till / paybill no."><input className={input} value={f.till_or_paybill || ""} onChange={(x) => setF({ ...f, till_or_paybill: x.target.value })} /></Field>
        <Field label="M-Pesa code of the till payment"><input className={input} value={f.receipt_ref || ""} onChange={(x) => setF({ ...f, receipt_ref: x.target.value })} /></Field>
      </div>
      <div className="mt-3"><Attach value={f.attachments} onChange={(v) => setF({ ...f, attachments: v })} label="M-Pesa message / receipt" /></div>
      <div className="mt-4 flex gap-2">
        <Btn kind="primary" onClick={async () => { if (await act(() => api.post(`/costs/spend/${e.id}/account`, { ...f, spent: n(f.spent), returned: n(f.returned) || 0 }), "Accounted for")) done(); }}>Save</Btn>
        <Btn onClick={done}>Cancel</Btn>
      </div>
    </Card>
  );
}

function Relays() {
  const { data } = useLoad("/costs/relays");
  if (!data) return <Loading />;
  return (
    <Card title={`Money still with staff: KES ${k0(data.total)}`} className="mt-4 bg-white">
      <Table minWidth={560} cols={[{ label: "Who" }, { label: "Relays", align: "right" }, { label: "Amount", align: "right" }, { label: "Oldest (days)", align: "right" }]}>
        {data.by_person.map((p) => <Tr key={p.who}><Td>{p.who}</Td><Td right>{p.count}</Td><Td right>{k0(p.amount)}</Td><Td right className={p.oldest_days > 3 ? "text-rose-700" : ""}>{p.oldest_days}</Td></Tr>)}
      </Table>
    </Card>
  );
}

function Float() {
  const can = useCan();
  const { data, reload } = useLoad("/costs/float");
  const [t, setT] = useState({ date: today(), source: "bank" });
  if (!data) return <Loading />;
  return (
    <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_380px]">
      <Card title="M-Pesa float (3015987)" className="bg-white">
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><div className="text-xs text-slate-500">Balance</div><div className="text-xl font-semibold">{k0(data.balance)}</div></div>
          <div><div className="text-xs text-slate-500">Approved, not yet paid</div><div className="text-xl font-semibold">{k0(data.approved_unpaid)}</div></div>
          <div><div className="text-xs text-slate-500">Topped up</div><div className="text-xl font-semibold">{k0(data.topups)}</div></div>
          <div><div className="text-xs text-slate-500">Paid out</div><div className="text-xl font-semibold">{k0(data.paid)}</div></div>
        </div>
        <Table minWidth={480} cols={[{ label: "Date" }, { label: "From" }, { label: "Reference" }, { label: "Amount", align: "right" }]}>
          {data.topups_list.map((x) => <Tr key={x.id}><Td>{fmtDate(x.date)}</Td><Td>{x.source}</Td><Td>{x.reference}</Td><Td right>{k0(x.amount)}</Td></Tr>)}
        </Table>
      </Card>
      {can("float.topup") && (
        <Card title="Record a top-up" className="h-fit bg-white">
          <div className="mt-3 space-y-3">
            <Field label="Date"><DateIn value={t.date} onChange={(v) => setT({ ...t, date: v })} /></Field>
            <Field label="Amount"><Num value={t.amount} onChange={(v) => setT({ ...t, amount: v })} /></Field>
            <Field label="From"><input className={input} value={t.source} onChange={(e) => setT({ ...t, source: e.target.value })} /></Field>
            <Field label="Reference"><input className={input} value={t.reference || ""} onChange={(e) => setT({ ...t, reference: e.target.value })} /></Field>
            <Btn kind="primary" onClick={async () => { if (await act(() => api.post("/costs/float/topups", { ...t, amount: n(t.amount) }), "Top-up recorded")) { setT({ date: today(), source: "bank" }); reload(); } }}>Save</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
